import { useState } from 'react';
import {
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconDownload, IconSearch } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { getToken } from '@/lib/token';
import { CallDetailModal } from './-components/call-detail-modal';
import { CallErrorBadge } from './-components/call-error-badge';
import {
  LiveCallsSubscription,
  OrganizationFlowsSubscription,
  OrganizationPhoneNumbersSubscription,
  SearchCallLogsCountSubscription,
  SearchCallLogsSubscription,
} from './-components/call-graphql';
import { callStatusColor, formatDuration, formatElapsed, formatPhone, formatWhen } from './-components/format';
import { useCallErrorSummary } from './-components/use-call-error-summary';
import { useNow } from './-components/use-now';
import { useRequireAdmin } from './-components/use-require-admin';
import { useSince } from './-components/use-time-window';

const PAGE_SIZE = 50;
const LIVE_CALL_MAX_AGE_MS = 12 * 60 * 60 * 1000;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Live' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'busy', label: 'Busy' },
  { value: 'transferred', label: 'Transferred' },
  { value: 'reaped', label: 'Reaped' },
];

const DIRECTION_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
];

type CallRow = {
  id: string;
  callControlId: string;
  fromNumber: string;
  toNumber: string;
  direction: string;
  flowName: string | null;
  phoneNumberLabel: string | null;
  startedAt: string;
  isLive: boolean;
  status: string;
  durationSeconds: number;
};

export const Route = createFileRoute('/_authed/calls')({
  component: CallsPage,
});

function normalizeNumber(input: string) {
  const digits = input.replace(/\D/g, '');

  return digits.length < 10 ? null : `+1${digits.slice(-10)}`;
}

function startOfDay(value: string) {
  return new Date(new Date(value).setHours(0, 0, 0, 0)).toISOString();
}

function endOfDay(value: string) {
  return new Date(new Date(value).setHours(23, 59, 59, 999)).toISOString();
}

function eqFilter(value: string | null) {
  return value ? { _eq: value } : undefined;
}

async function downloadCallsExport(params: URLSearchParams) {
  const response = await fetch(`/api/export/calls?${params}`, {
    headers: { authorization: `Bearer ${getToken() ?? ''}` },
  });

  if (!response.ok) throw new Error(await response.text());

  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = `calls-export-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function Calls({ organizationId }: { organizationId: string }) {
  const [status, setStatus] = useState('all');
  const [direction, setDirection] = useState('all');
  const [flowId, setFlowId] = useState<string | null>(null);
  const [phoneNumberId, setPhoneNumberId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [numberSearch, setNumberSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedCallControlId, setSelectedCallControlId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const now = useNow();
  const liveFloor = useSince(LIVE_CALL_MAX_AGE_MS);

  const [{ data: flowData }] = useSubscription({ query: OrganizationFlowsSubscription, variables: { organizationId } });
  const [{ data: phoneData }] = useSubscription({
    query: OrganizationPhoneNumbersSubscription,
    variables: { organizationId },
  });

  const normalizedSearch = numberSearch.trim() ? normalizeNumber(numberSearch) : null;
  const startedAtFrom = dateRange[0] ? startOfDay(dateRange[0]) : null;
  const startedAtTo = dateRange[1] ? endOfDay(dateRange[1]) : null;
  const showLive = status === 'all' || status === 'active';

  const startedAtFilter =
    startedAtFrom || startedAtTo
      ? { ...(startedAtFrom ? { _gte: startedAtFrom } : {}), ...(startedAtTo ? { _lte: startedAtTo } : {}) }
      : undefined;

  const [{ data: liveData }] = useSubscription({
    query: LiveCallsSubscription,
    variables: {
      where: {
        organizationId: { _eq: organizationId },
        status: { _eq: 'active' },
        startedAt: startedAtFilter ?? { _gte: liveFloor },
        direction: eqFilter(direction === 'all' ? null : direction),
        flowId: eqFilter(flowId),
        phoneNumberId: eqFilter(phoneNumberId),
        fromNumber: eqFilter(normalizedSearch),
      },
    },
    pause: !showLive,
  });

  const historyWhere = {
    organizationId: { _eq: organizationId },
    status: eqFilter(status === 'all' || status === 'active' ? null : status),
    direction: eqFilter(direction === 'all' ? null : direction),
    flowId: eqFilter(flowId),
    phoneNumberId: eqFilter(phoneNumberId),
    fromNumber: eqFilter(normalizedSearch),
    startedAt: startedAtFilter,
  };

  const [{ data: historyData }] = useSubscription({
    query: SearchCallLogsSubscription,
    variables: { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, where: historyWhere },
    pause: status === 'active',
  });

  const [{ data: historyCountData }] = useSubscription({
    query: SearchCallLogsCountSubscription,
    variables: { where: historyWhere },
    pause: status === 'active',
  });

  const historyRows: CallRow[] = (historyData?.callLogs ?? []).map((log) => ({
    id: log.id,
    callControlId: log.callControlId,
    fromNumber: log.fromNumber,
    toNumber: log.toNumber,
    direction: log.direction,
    flowName: log.flow?.name ?? null,
    phoneNumberLabel: log.phoneNumber?.friendlyName ?? log.phoneNumber?.phoneNumber ?? null,
    startedAt: log.startedAt,
    isLive: false,
    status: log.status,
    durationSeconds: log.durationSeconds,
  }));

  const historyControlIds = new Set(historyRows.map((r) => r.callControlId));
  const liveRows: CallRow[] = (liveData?.callSessions ?? [])
    .filter((s) => !historyControlIds.has(s.callControlId))
    .map((s) => ({
      id: s.id,
      callControlId: s.callControlId,
      fromNumber: s.fromNumber,
      toNumber: s.toNumber,
      direction: s.direction,
      flowName: s.flow?.name ?? null,
      phoneNumberLabel: s.phoneNumber?.friendlyName ?? s.phoneNumber?.phoneNumber ?? null,
      startedAt: s.startedAt,
      isLive: true,
      status: 'active',
      durationSeconds: 0,
    }));

  const rows = page === 1 ? [...liveRows, ...historyRows] : historyRows;
  const errorSummary = useCallErrorSummary(
    organizationId,
    rows.map((r) => r.callControlId),
  );

  const totalHistory = historyCountData?.callLogsAggregate.aggregate?.count ?? 0;
  const isLoadingFirst = status !== 'active' && (historyData === undefined || historyCountData === undefined);

  const handleExport = async () => {
    setIsExporting(true);
    setExportError(null);

    const params = new URLSearchParams({ organizationId });

    if (status !== 'all' && status !== 'active') params.set('status', status);
    if (direction !== 'all') params.set('direction', direction);
    if (flowId) params.set('flowId', flowId);
    if (phoneNumberId) params.set('phoneNumberId', phoneNumberId);
    if (startedAtFrom) params.set('startDate', String(new Date(startedAtFrom).getTime()));
    if (startedAtTo) params.set('endDate', String(new Date(startedAtTo).getTime()));
    if (numberSearch.trim()) params.set('numberSearch', numberSearch.trim());

    try {
      await downloadCallsExport(params);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const resetPage = <T,>(setter: (value: T) => void) => {
    return (value: T) => {
      setPage(1);
      setter(value);
    };
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>Calls</Title>
          <Text c="dimmed" size="sm">
            Search and inspect every call
          </Text>
        </div>

        <Paper p="md" radius="lg" withBorder>
          <Stack gap="md">
            <Group align="flex-end" gap="md" wrap="wrap">
              <TextInput
                label="Caller number"
                placeholder="Search by caller number"
                leftSection={<IconSearch size={16} />}
                value={numberSearch}
                onChange={(e) => resetPage(setNumberSearch)(e.currentTarget.value)}
                w={240}
              />
              <Select
                label="Status"
                data={STATUS_OPTIONS}
                value={status}
                onChange={(v) => resetPage(setStatus)(v ?? 'all')}
                w={150}
              />
              <Select
                label="Direction"
                data={DIRECTION_OPTIONS}
                value={direction}
                onChange={(v) => resetPage(setDirection)(v ?? 'all')}
                w={140}
              />
            </Group>
            <Group align="flex-end" gap="md" wrap="wrap">
              <Select
                label="Flow"
                placeholder="All flows"
                data={(flowData?.flows ?? []).map((f) => ({ value: f.id, label: f.name }))}
                value={flowId}
                onChange={resetPage(setFlowId)}
                clearable
                searchable
                w={220}
              />
              <Select
                label="Phone number"
                placeholder="All numbers"
                data={(phoneData?.phoneNumbers ?? []).map((p) => ({
                  value: p.id,
                  label: p.friendlyName ?? formatPhone(p.phoneNumber),
                }))}
                value={phoneNumberId}
                onChange={resetPage(setPhoneNumberId)}
                clearable
                searchable
                w={220}
              />
              <DatePickerInput
                type="range"
                label="Date range"
                placeholder="Any time"
                value={dateRange}
                onChange={resetPage(setDateRange)}
                clearable
                w={260}
              />
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={() => void handleExport()}
                loading={isExporting}
                disabled={status === 'active'}
                ml="auto"
              >
                Export CSV
              </Button>
            </Group>
            {exportError && (
              <Text size="xs" c="red">
                Export failed: {exportError}
              </Text>
            )}
          </Stack>
        </Paper>

        {isLoadingFirst ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : rows.length === 0 ? (
          <Paper p="xl" radius="lg" withBorder>
            <Text c="dimmed" size="sm" ta="center">
              No calls match these filters.
            </Text>
          </Paper>
        ) : (
          <Paper radius="lg" withBorder>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>When</Table.Th>
                  <Table.Th>From</Table.Th>
                  <Table.Th>To</Table.Th>
                  <Table.Th>Flow</Table.Th>
                  <Table.Th>Direction</Table.Th>
                  <Table.Th>Duration</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => (
                  <Table.Tr
                    key={row.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCallControlId(row.callControlId)}
                  >
                    <Table.Td>{row.isLive ? 'In progress' : formatWhen(row.startedAt)}</Table.Td>
                    <Table.Td>{formatPhone(row.fromNumber)}</Table.Td>
                    <Table.Td>{row.phoneNumberLabel ?? formatPhone(row.toNumber)}</Table.Td>
                    <Table.Td>{row.flowName ?? '—'}</Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={row.direction === 'inbound' ? 'blue' : 'grape'}>
                        {row.direction}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {row.isLive ? formatElapsed(row.startedAt, now) : formatDuration(row.durationSeconds)}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Badge size="sm" variant="filled" color={callStatusColor(row.status)}>
                          {row.isLive ? 'live' : row.status}
                        </Badge>
                        <CallErrorBadge summary={errorSummary.get(row.callControlId)} hint="click to view" />
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {status !== 'active' && totalHistory > PAGE_SIZE && (
              <Group justify="center" p="md">
                <Pagination value={page} onChange={setPage} total={Math.ceil(totalHistory / PAGE_SIZE)} size="sm" />
              </Group>
            )}
          </Paper>
        )}
      </Stack>

      <CallDetailModal
        organizationId={organizationId}
        callControlId={selectedCallControlId}
        onClose={() => setSelectedCallControlId(null)}
      />
    </Container>
  );
}

function CallsPage() {
  const { organizationId, isAdmin, isLoading } = useRequireAdmin();

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!organizationId || !isAdmin) {
    return (
      <Center h="50vh">
        <Text c="dimmed">No organization selected.</Text>
      </Center>
    );
  }

  return <Calls organizationId={organizationId} />;
}
